// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import KeybindManager from '@app/features/app/keybindings/KeybindManager';
import {KeybindRecorder} from '@app/features/input/components/KeybindRecorder';
import Keybind, {
	type CustomKeybindEntry,
	type KeybindCommand,
	type KeybindConfig,
	type KeybindSection,
	type KeyCombo,
} from '@app/features/input/state/Keybind';
import {formatKeyCombo} from '@app/features/input/utils/KeybindUtils';
import {
	EditSimpleIcon,
	HideIcon,
	MoreOptionsVerticalIcon,
	RetryIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {Button} from '@app/features/ui/button/Button';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Input} from '@app/features/ui/components/form/Input';
import {Select, type SelectOption} from '@app/features/ui/components/form/Select';
import {Switch} from '@app/features/ui/components/form/Switch';
import {InputMonitoringSection} from '@app/features/user/components/modals/tabs/components/InputMonitoringSection';
import styles from '@app/features/user/components/modals/tabs/KeybindsTab.module.css';
import {Trans, useLingui} from '@lingui/react/macro';
import {InfoIcon, MagnifyingGlassIcon, PlusIcon, TrashIcon, WarningIcon} from '@phosphor-icons/react';
import clsx from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const DEFAULT_KEYBIND_SECTIONS: ReadonlyArray<KeybindSection> = [
	'defaults',
	'messages',
	'navigation',
	'drag_and_drop',
	'chat',
	'voice_and_video',
	'misc',
];
const SECTION_DISPLAY_ORDER: Partial<Record<KeybindSection, ReadonlyArray<KeybindCommand>>> = {
	messages: [
		'message_reply',
		'message_edit',
		'message_delete',
		'message_react',
		'message_forward',
		'message_pin',
		'message_mark_unread',
		'message_copy_text',
		'message_speak',
		'message_focus_textarea',
	],
	navigation: [
		'nav_quick_switcher',
		'nav_guild_prev',
		'nav_guild_next',
		'nav_channel_prev',
		'nav_channel_next',
		'nav_toggle_last_guild_dms',
		'nav_history_back',
		'nav_history_forward',
		'nav_unread_prev',
		'nav_unread_next',
		'nav_mention_prev',
		'nav_mention_next',
		'nav_current_call',
		'nav_add_guild',
	],
	drag_and_drop: ['dnd_start', 'dnd_move_up', 'dnd_move_down', 'dnd_drop', 'dnd_cancel'],
	chat: [
		'chat_focus_textarea',
		'chat_upload',
		'chat_toggle_emoji',
		'chat_toggle_gif',
		'chat_toggle_sticker',
		'chat_toggle_saved_media',
		'chat_toggle_pins',
		'chat_toggle_inbox',
		'chat_toggle_member_list',
		'chat_scroll_up',
		'chat_scroll_down',
		'chat_jump_oldest_unread',
		'chat_mark_channel_read',
		'chat_mark_guild_read',
		'chat_mark_inbox_read',
		'chat_mark_all_inbox_read',
		'chat_new_dm',
		'chat_copy_channel_link',
	],
	voice_and_video: [
		'voice_toggle_mute',
		'voice_toggle_deafen',
		'voice_toggle_soundboard',
		'voice_start_dm_call',
		'voice_answer_call',
		'voice_decline_call',
	],
	misc: ['misc_search', 'misc_open_context_menu', 'misc_help'],
};
function sortBySectionDisplayOrder(
	section: KeybindSection,
	entries: ReadonlyArray<KeybindConfig>,
): Array<KeybindConfig> {
	const order = SECTION_DISPLAY_ORDER[section];
	if (!order) return [...entries];
	const indexFor = (action: KeybindCommand) => {
		const idx = order.indexOf(action);
		return idx === -1 ? Number.POSITIVE_INFINITY : idx;
	};
	return [...entries].sort((a, b) => indexFor(a.action) - indexFor(b.action));
}
const SHORTCUT_MERGE_PAIRS: ReadonlyArray<readonly [KeybindCommand, KeybindCommand]> = [
	['nav_guild_prev', 'nav_guild_next'],
	['nav_channel_prev', 'nav_channel_next'],
	['nav_history_back', 'nav_history_forward'],
	['nav_unread_prev', 'nav_unread_next'],
	['nav_mention_prev', 'nav_mention_next'],
	['chat_scroll_up', 'chat_scroll_down'],
	['dnd_move_up', 'dnd_move_down'],
];

type ShortcutRowModel = KeybindConfig | [KeybindConfig, KeybindConfig];
function isShortcutMergePair(row: ShortcutRowModel): row is [KeybindConfig, KeybindConfig] {
	return Array.isArray(row);
}
function chipsForDefaultEntry(entry: KeybindConfig): Array<string> {
	if (entry.defaultsShortcutDisplayKind === 'any_key') {
		return ['ANY KEY'];
	}
	if (entry.defaultsShortcutDisplayKind === 'space_or_enter') {
		const space = formatKeyCombo({key: ' '}).split(' + ');
		const enter = formatKeyCombo({key: 'Enter'}).split(' + ');
		return [...space, ...enter];
	}
	const combo = entry.combo;
	if (!combo) return [];
	if (combo.modifierOnly && combo.bothSides) {
		const formatted = formatKeyCombo(combo);
		return formatted ? formatted.split(' + ') : [];
	}
	const formatted = formatKeyCombo(combo);
	if (!formatted) return [];
	return formatted.split(' + ');
}
function partitionMergedShortcutRows(entries: ReadonlyArray<KeybindConfig>): Array<ShortcutRowModel> {
	const result: Array<ShortcutRowModel> = [];
	let i = 0;
	while (i < entries.length) {
		const pair = SHORTCUT_MERGE_PAIRS.find(([a, b]) => entries[i]?.action === a && entries[i + 1]?.action === b);
		if (pair && entries[i + 1]) {
			result.push([entries[i], entries[i + 1]]);
			i += 2;
			continue;
		}
		result.push(entries[i]);
		i += 1;
	}
	return result;
}

const UNASSIGNED = '__unassigned__' as const;

type ActionValue = KeybindCommand | typeof UNASSIGNED;

type ActionOption = SelectOption<ActionValue>;

const ACTION_DROPDOWN_MENU_MIN_WIDTH = 300;
const FEATURED_CUSTOM_KEYBIND_ACTIONS: ReadonlyArray<KeybindCommand> = [
	'voice_push_to_talk',
	'voice_push_to_talk_priority',
	'voice_push_to_mute',
	'voice_priority_vad',
	'voice_toggle_mute',
	'voice_toggle_deafen',
	'voice_toggle_vad',
	'voice_toggle_camera',
	'voice_switch_channel',
	'nav_history_back',
	'nav_history_forward',
];
const useAssignableActionOptions = (currentAction?: KeybindCommand | null): ReadonlyArray<ActionOption> => {
	const {t} = useLingui();
	return useMemo(() => {
		const defaults = Keybind.getDefaults();
		const labelByAction = new Map<KeybindCommand, string>();
		for (const entry of defaults) {
			labelByAction.set(entry.action, entry.label);
		}
		const options: Array<ActionOption> = [{value: UNASSIGNED, label: t`Unassigned`}];
		const includedActions = new Set<KeybindCommand>();
		const appendAction = (action: KeybindCommand) => {
			if (includedActions.has(action)) return;
			let label = labelByAction.get(action) ?? action;
			if (action === 'nav_history_back') {
				label = t`Move back through page history`;
			} else if (action === 'nav_history_forward') {
				label = t`Move forward through page history`;
			}
			if (!label) return;
			options.push({value: action, label});
			includedActions.add(action);
		};
		for (const action of FEATURED_CUSTOM_KEYBIND_ACTIONS) {
			appendAction(action);
		}
		for (const entry of defaults) {
			if (entry.informationalOnly) continue;
			appendAction(entry.action);
		}
		if (currentAction) {
			appendAction(currentAction);
		}
		return options;
	}, [currentAction, t]);
};
const ActionDropdown: React.FC<{
	value: KeybindCommand | null;
	options: ReadonlyArray<ActionOption>;
	onChange: (value: KeybindCommand | null) => void;
	ariaLabel: string;
}> = ({value, options, onChange, ariaLabel}) => {
	return (
		<Select<ActionValue>
			value={value ?? UNASSIGNED}
			options={options}
			onChange={(next) => onChange(next === UNASSIGNED ? null : next)}
			isSearchable={false}
			density="compact"
			menuMinWidth={ACTION_DROPDOWN_MENU_MIN_WIDTH}
			placeholder={ariaLabel}
		/>
	);
};
const CustomKeybindRow = observer(
	({
		entry,
		onRemove,
		conflictLabel,
	}: {
		entry: CustomKeybindEntry;
		onRemove: () => void;
		conflictLabel: string | null;
	}) => {
		const {t} = useLingui();
		const options = useAssignableActionOptions(entry.action);
		const handleAction = (action: KeybindCommand | null) => {
			Keybind.setCustomKeybindAction(entry.id, action);
		};
		const handleCombo = (combo: KeyCombo) => {
			Keybind.updateCustomKeybindCombo(entry.id, {...combo, global: combo.global ?? entry.combo.global ?? true});
		};
		const handleClear = () => {
			Keybind.updateCustomKeybindCombo(entry.id, {key: '', code: '', global: entry.combo.global, enabled: true});
		};
		const handleEnabled = (value: boolean) => {
			Keybind.setCustomKeybindEnabled(entry.id, value);
		};
		const openRowMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
			ContextMenuCommands.openFromEvent(event, ({onClose}) => (
				<MenuGroup>
					<MenuItem
						icon={entry.enabled ? <HideIcon size={16} /> : <EditSimpleIcon size={16} />}
						onClick={() => {
							onClose();
							handleEnabled(!entry.enabled);
						}}
					>
						{entry.enabled ? t`Disable keybind` : t`Enable keybind`}
					</MenuItem>
					<MenuItem
						icon={<TrashIcon size={16} />}
						danger
						onClick={() => {
							onClose();
							onRemove();
						}}
					>
						{t`Remove keybind`}
					</MenuItem>
				</MenuGroup>
			));
		};
		return (
			<div className={clsx(styles.customRow, !entry.enabled && styles.customRowDisabled)} data-keybind-id={entry.id}>
				<div className={styles.customRowHeader}>
					<div className={styles.customRowStatus} aria-hidden>
						<span className={clsx(styles.statusDot, entry.enabled ? styles.statusDotOn : styles.statusDotOff)} />
						<span className={styles.statusLabel}>{entry.enabled ? t`Enabled` : t`Disabled`}</span>
					</div>
					<button
						type="button"
						className={styles.dotsButton}
						onClick={openRowMenu}
						aria-label={t`Custom keybind options`}
					>
						<MoreOptionsVerticalIcon size={18} />
					</button>
				</div>
				<div className={styles.customRowMain}>
					<div className={styles.customField}>
						<div className={styles.customFieldLabel}>
							<Trans>Action</Trans>
						</div>
						<ActionDropdown
							value={entry.action}
							options={options}
							onChange={handleAction}
							ariaLabel={t`Custom keybind action`}
						/>
					</div>
					<div className={styles.customField}>
						<div className={styles.customFieldLabel}>
							<Trans>Keybind</Trans>
						</div>
						<KeybindRecorder
							action={entry.action ?? 'voice_push_to_talk'}
							value={entry.combo}
							onChange={handleCombo}
							onClear={handleClear}
						/>
					</div>
				</div>
				{!entry.action ? (
					<div className={styles.customHint}>
						<Trans>Pick an action from the dropdown.</Trans>
					</div>
				) : null}
				{conflictLabel ? (
					<div className={styles.conflictWarning} role="alert">
						<WarningIcon size={14} weight="fill" className={styles.conflictIcon} aria-hidden />
						<span>
							<Trans>Conflicts with {conflictLabel}</Trans>
						</span>
					</div>
				) : null}
			</div>
		);
	},
);

const OVERRIDDEN_CHIP_STYLE: React.CSSProperties = {opacity: 0.5, textDecoration: 'line-through'};
const DefaultShortcutChipList: React.FC<{chips: Array<string>; overridden?: boolean}> = ({chips, overridden}) => (
	<div className={styles.defaultChips}>
		{chips.length === 0 ? (
			<span className={styles.defaultChipsEmpty}>
				<Trans>Unassigned</Trans>
			</span>
		) : (
			chips.map((chip, idx) => (
				<span
					key={`${idx}-${chip}`}
					className={styles.defaultChip}
					style={overridden ? OVERRIDDEN_CHIP_STYLE : undefined}
				>
					{chip === 'ANY KEY' ? <Trans>Any key</Trans> : chip}
				</span>
			))
		)}
		{overridden ? (
			<span className={styles.defaultChipsEmpty}>
				<Trans>Overridden</Trans>
			</span>
		) : null}
	</div>
);
function getRowActions(row: ShortcutRowModel): Array<KeybindConfig> {
	return isShortcutMergePair(row) ? [...row] : [row];
}
const DefaultShortcutRow: React.FC<{row: ShortcutRowModel; overriddenActions: ReadonlySet<KeybindCommand>}> = ({
	row,
	overriddenActions,
}) => {
	const {t} = useLingui();
	const actions = getRowActions(row);
	const openRowMenu = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			ContextMenuCommands.openFromEvent(event, ({onClose}) => {
				const perAction = actions.map((action) => {
					const customBindings = Keybind.getCustomKeybinds().filter((entry) => entry.action === action.action);
					const hasEnabledCustomBinding = customBindings.some((entry) => entry.enabled);
					return {action, customBindings, hasEnabledCustomBinding};
				});
				const allHaveCustom = perAction.every((p) => p.customBindings.length > 0);
				const noneHaveCustom = perAction.every((p) => p.customBindings.length === 0);
				if (noneHaveCustom) {
					return (
						<MenuGroup>
							<MenuItem
								icon={<EditSimpleIcon size={16} />}
								onClick={() => {
									onClose();
									for (const {action} of perAction) {
										Keybind.addCustomKeybindForAction(action.action);
									}
								}}
							>
								{t`Set custom shortcut`}
							</MenuItem>
							<MenuItem
								icon={<HideIcon size={16} />}
								onClick={() => {
									onClose();
									for (const {action} of perAction) {
										Keybind.addCustomKeybindForAction(action.action);
									}
								}}
							>
								{t`Disable built-in shortcut`}
							</MenuItem>
						</MenuGroup>
					);
				}
				if (allHaveCustom && perAction.length === 1) {
					const {action, hasEnabledCustomBinding} = perAction[0];
					return (
						<MenuGroup>
							{hasEnabledCustomBinding ? (
								<MenuItem
									icon={<RetryIcon size={16} />}
									danger
									onClick={() => {
										onClose();
										Keybind.removeCustomKeybindsForAction(action.action);
									}}
								>
									{t`Reset to built-in`}
								</MenuItem>
							) : (
								<MenuItem
									icon={<EditSimpleIcon size={16} />}
									onClick={() => {
										onClose();
										Keybind.addCustomKeybindForAction(action.action);
									}}
								>
									{t`Add another custom shortcut`}
								</MenuItem>
							)}
						</MenuGroup>
					);
				}
				return (
					<>
						{perAction.map(({action, customBindings, hasEnabledCustomBinding}) => (
							<MenuGroup key={action.action}>
								{customBindings.length > 0 && hasEnabledCustomBinding ? (
									<MenuItem
										icon={<RetryIcon size={16} />}
										danger
										onClick={() => {
											onClose();
											Keybind.removeCustomKeybindsForAction(action.action);
										}}
									>
										{t`Reset "${action.label}" to built-in`}
									</MenuItem>
								) : (
									<MenuItem
										icon={<EditSimpleIcon size={16} />}
										onClick={() => {
											onClose();
											Keybind.addCustomKeybindForAction(action.action);
										}}
									>
										{t`Set custom shortcut for "${action.label}"`}
									</MenuItem>
								)}
							</MenuGroup>
						))}
					</>
				);
			});
		},
		[actions, t],
	);
	if (isShortcutMergePair(row)) {
		const [a, b] = row;
		return (
			<div className={styles.defaultRow}>
				<div className={styles.defaultLabel}>{a.label}</div>
				<div className={styles.defaultRowActions}>
					<div className={styles.defaultChipsMerged}>
						<DefaultShortcutChipList chips={chipsForDefaultEntry(a)} overridden={overriddenActions.has(a.action)} />
						<DefaultShortcutChipList chips={chipsForDefaultEntry(b)} overridden={overriddenActions.has(b.action)} />
					</div>
					<button
						type="button"
						className={styles.dotsButton}
						onClick={openRowMenu}
						aria-label={t`More keybind options`}
					>
						<MoreOptionsVerticalIcon size={18} />
					</button>
				</div>
			</div>
		);
	}
	const entry = row;
	return (
		<div className={styles.defaultRow}>
			<div className={styles.defaultLabel}>{entry.label}</div>
			<div className={styles.defaultRowActions}>
				<DefaultShortcutChipList chips={chipsForDefaultEntry(entry)} overridden={overriddenActions.has(entry.action)} />
				<button type="button" className={styles.dotsButton} onClick={openRowMenu} aria-label={t`More keybind options`}>
					<MoreOptionsVerticalIcon size={18} />
				</button>
			</div>
		</div>
	);
};
function normalizeQuery(query: string): string {
	return query.trim().toLowerCase();
}
function entryMatchesQuery(entry: KeybindConfig, normalized: string): boolean {
	if (!normalized) return true;
	if (entry.label.toLowerCase().includes(normalized)) return true;
	const chips = chipsForDefaultEntry(entry);
	for (const chip of chips) {
		if (chip.toLowerCase().includes(normalized)) return true;
	}
	return false;
}
function comboMatchesQuery(combo: KeyCombo, normalized: string): boolean {
	if (!normalized) return true;
	const formatted = formatKeyCombo(combo);
	if (formatted?.toLowerCase().includes(normalized)) return true;
	if (combo.key?.toLowerCase().includes(normalized)) return true;
	return false;
}
const DefaultKeybindsList: React.FC<{searchQuery: string}> = observer(({searchQuery}) => {
	const {t} = useLingui();
	const defaults = Keybind.getDefaults();
	const customKeybinds = Keybind.getCustomKeybinds();
	const overriddenActions = useMemo(() => {
		const set = new Set<KeybindCommand>();
		for (const entry of customKeybinds) {
			if (entry.action && entry.enabled) set.add(entry.action);
		}
		return set;
	}, [customKeybinds]);
	const normalized = normalizeQuery(searchQuery);
	const sectionLabels: Record<KeybindSection, string> = {
		defaults: t`Default keybinds`,
		messages: t`Messages`,
		navigation: t`Navigation`,
		drag_and_drop: t`Drag and drop`,
		chat: t`Chat`,
		voice_and_video: t`Voice and video`,
		misc: t`Miscellaneous`,
	};
	const sectionSubtitles: Partial<Record<KeybindSection, string>> = {
		messages: t`These shortcuts apply while a message is focused.`,
		drag_and_drop: t`These shortcuts apply while a draggable item is focused.`,
	};
	const sections = useMemo(() => {
		const map: Record<KeybindSection, Array<KeybindConfig>> = {
			defaults: [],
			messages: [],
			navigation: [],
			drag_and_drop: [],
			chat: [],
			voice_and_video: [],
			misc: [],
		};
		for (const entry of defaults) {
			if (entry.hideFromDefaults) continue;
			if (!entryMatchesQuery(entry, normalized)) continue;
			map[entry.section].push(entry);
		}
		return map;
	}, [defaults, normalized]);
	const hasAny = DEFAULT_KEYBIND_SECTIONS.some((sectionId) => sections[sectionId].length > 0);
	if (!hasAny && normalized) {
		return (
			<div className={styles.defaultsList}>
				<div className={styles.emptyState}>
					<Trans>No default keybinds match your search.</Trans>
				</div>
			</div>
		);
	}
	return (
		<div className={styles.defaultsList}>
			{DEFAULT_KEYBIND_SECTIONS.map((sectionId) => {
				const entries = sections[sectionId];
				if (entries.length === 0) return null;
				const orderedEntries = sortBySectionDisplayOrder(sectionId, entries);
				const rows = partitionMergedShortcutRows(orderedEntries);
				const subtitle = sectionSubtitles[sectionId];
				return (
					<div className={styles.defaultsSection} key={sectionId}>
						<div className={styles.defaultsSectionTitle}>{sectionLabels[sectionId]}</div>
						{subtitle ? <div className={styles.defaultsSectionSubtitle}>{subtitle}</div> : null}
						<div className={styles.defaultsSectionRows}>
							{rows.map((row, idx) => (
								<DefaultShortcutRow
									key={Array.isArray(row) ? `${row[0].action}-${row[1].action}-${idx}` : `${row.action}-${idx}`}
									row={row}
									overriddenActions={overriddenActions}
								/>
							))}
						</div>
					</div>
				);
			})}
		</div>
	);
});
const CustomKeybindsList: React.FC<{searchQuery: string}> = observer(({searchQuery}) => {
	const {t} = useLingui();
	const customKeybinds = Keybind.getCustomKeybinds();
	const syncAcrossDevices = Keybind.getSyncAcrossDevices();
	const disableBuiltinKeybinds = Keybind.getDisableBuiltinKeybinds();
	const labelByAction = new Map<KeybindCommand, string>();
	for (const entry of Keybind.getDefaults()) {
		labelByAction.set(entry.action, entry.label);
	}
	const normalized = normalizeQuery(searchQuery);
	const filteredKeybinds = useMemo(() => {
		if (!normalized) return customKeybinds;
		return customKeybinds.filter((entry) => {
			const label = entry.action ? (labelByAction.get(entry.action) ?? entry.action) : '';
			if (label.toLowerCase().includes(normalized)) return true;
			return comboMatchesQuery(entry.combo, normalized);
		});
	}, [customKeybinds, labelByAction, normalized]);
	const conflictLabels = useMemo(() => {
		const result = new Map<string, string>();
		for (let i = 0; i < customKeybinds.length; i++) {
			const target = customKeybinds[i];
			if (!target.enabled || !target.combo.key) continue;
			for (let j = 0; j < customKeybinds.length; j++) {
				if (i === j) continue;
				const other = customKeybinds[j];
				if (!other.enabled || !other.combo.key) continue;
				if (combosLooseEqual(target.combo, other.combo)) {
					const otherLabel = other.action ? (labelByAction.get(other.action) ?? other.action) : t`(Unassigned)`;
					result.set(target.id, otherLabel);
					break;
				}
			}
		}
		return result;
	}, [customKeybinds, labelByAction, t]);
	const handleAdd = () => {
		Keybind.addCustomKeybind();
	};
	const handleResetAll = () => {
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={t`Reset all keybinds?`}
					description={t`This removes every custom keybind and re-enables all built-in shortcuts. This cannot be undone.`}
					primaryText={t`Reset to defaults`}
					primaryVariant="danger-primary"
					onPrimary={() => {
						Keybind.resetCustomKeybinds();
						Keybind.setDisableBuiltinKeybinds(false);
					}}
				/>
			)),
		);
	};
	const hasAnyOverrides = customKeybinds.length > 0 || disableBuiltinKeybinds;
	return (
		<div className={styles.customSection}>
			<div className={styles.customHeader}>
				<div className={styles.customNotice}>
					<InfoIcon size={16} weight="fill" className={styles.customNoticeIcon} />
					<Trans>
						Shortcuts are paused while this panel is open. Adding a custom keybind for an action replaces its default —
						leave the combo blank to disable the default outright.
					</Trans>
				</div>
				<div className={styles.customHeaderActions}>
					{hasAnyOverrides ? (
						<Button variant="secondary" small type="button" onClick={handleResetAll} leftIcon={<RetryIcon size={14} />}>
							<Trans>Reset to defaults</Trans>
						</Button>
					) : null}
					<Button
						variant="primary"
						small
						type="button"
						onClick={handleAdd}
						leftIcon={<PlusIcon size={14} weight="bold" />}
					>
						<Trans>Add a keybind</Trans>
					</Button>
				</div>
			</div>
			<div className={styles.syncOption}>
				<Switch
					label={t`Disable built-in keybinds`}
					description={t`Stops every default shortcut from firing on this device, so combos like Ctrl+1–9 fall through to your browser's tab switching. Custom keybinds you add below still work.`}
					value={disableBuiltinKeybinds}
					onChange={(value) => Keybind.setDisableBuiltinKeybinds(value)}
				/>
			</div>
			<div className={styles.syncOption}>
				<Switch
					label={t`Sync keybinds across devices`}
					description={t`When enabled, custom keybinds and voice input keybind preferences will sync to your other devices. When disabled, this device keeps its own keybind settings.`}
					value={syncAcrossDevices}
					onChange={(value) => Keybind.setSyncAcrossDevices(value)}
				/>
			</div>
			<div className={styles.customList}>
				{customKeybinds.length === 0 ? (
					<div className={styles.emptyState}>
						<Trans>No custom keybinds yet.</Trans>
					</div>
				) : filteredKeybinds.length === 0 ? (
					<div className={styles.emptyState}>
						<Trans>No custom keybinds match your search.</Trans>
					</div>
				) : (
					filteredKeybinds.map((entry) => (
						<CustomKeybindRow
							key={entry.id}
							entry={entry}
							conflictLabel={conflictLabels.get(entry.id) ?? null}
							onRemove={() => Keybind.removeCustomKeybind(entry.id)}
						/>
					))
				)}
			</div>
		</div>
	);
});
function combosLooseEqual(a: KeyCombo, b: KeyCombo): boolean {
	if (!a || !b) return false;
	if ((a.key ?? '') !== (b.key ?? '')) return false;
	if ((a.code ?? '') !== (b.code ?? '')) return false;
	if (!!a.ctrlOrMeta !== !!b.ctrlOrMeta) return false;
	if (!!a.ctrl !== !!b.ctrl) return false;
	if (!!a.alt !== !!b.alt) return false;
	if (!!a.shift !== !!b.shift) return false;
	if (!!a.meta !== !!b.meta) return false;
	if (!!a.modifierOnly !== !!b.modifierOnly) return false;
	if (!!a.bothSides !== !!b.bothSides) return false;
	if ((a.mouseButton ?? null) !== (b.mouseButton ?? null)) return false;
	if ((a.gamepadButton ?? null) !== (b.gamepadButton ?? null)) return false;
	return true;
}
const PortalShortcutsList: React.FC = observer(() => {
	const bindings = Keybind.portalShortcutBindings;
	if (bindings.size === 0) return null;
	const defaults = Keybind.getDefaults();
	const entries: Array<{action: KeybindCommand; label: string; trigger: string}> = [];
	for (const def of defaults) {
		const trigger = bindings.get(def.action);
		if (trigger === undefined) continue;
		entries.push({action: def.action, label: def.label, trigger});
	}
	if (entries.length === 0) return null;
	return (
		<div className={styles.defaultsSection}>
			<div className={styles.defaultsSectionTitle}>
				<Trans>Desktop environment shortcuts</Trans>
			</div>
			<div className={styles.defaultsSectionSubtitle}>
				<Trans>
					On Wayland, global shortcuts are managed by your desktop environment. Open your DE's keyboard settings to
					assign or change these triggers.
				</Trans>
			</div>
			<div className={styles.defaultsSectionRows}>
				{entries.map(({action, label, trigger}) => (
					<div key={action} className={styles.defaultRow}>
						<div className={styles.defaultLabel}>{label}</div>
						{trigger ? (
							<div className={styles.defaultChips}>
								<span className={styles.defaultChip}>{trigger}</span>
							</div>
						) : (
							<span className={styles.defaultChipsEmpty}>
								<Trans>Unassigned</Trans>
							</span>
						)}
					</div>
				))}
			</div>
		</div>
	);
});
const PRINTABLE_NAV_KEYS_TO_SWALLOW = new Set([
	'Tab',
	'Escape',
	'F1',
	'F2',
	'F3',
	'F4',
	'F5',
	'F6',
	'F7',
	'F8',
	'F9',
	'F10',
	'F11',
	'F12',
	'PageUp',
	'PageDown',
	'Home',
	'End',
	'Insert',
	'ContextMenu',
]);
function isShortcutLikeKeyEvent(event: KeyboardEvent): boolean {
	if (event.ctrlKey || event.metaKey || event.altKey) return true;
	if (event.key.length > 1 && PRINTABLE_NAV_KEYS_TO_SWALLOW.has(event.key)) return true;
	return false;
}
const KeybindsTab: React.FC = observer(() => {
	const {t} = useLingui();
	useEffect(() => {
		KeybindManager.suspend();
		const swallowShortcut = (event: KeyboardEvent) => {
			if (!isShortcutLikeKeyEvent(event)) return;
			event.stopPropagation();
		};
		window.addEventListener('keydown', swallowShortcut, true);
		window.addEventListener('keyup', swallowShortcut, true);
		window.addEventListener('keypress', swallowShortcut, true);
		return () => {
			window.removeEventListener('keydown', swallowShortcut, true);
			window.removeEventListener('keyup', swallowShortcut, true);
			window.removeEventListener('keypress', swallowShortcut, true);
			KeybindManager.resume();
		};
	}, []);
	const containerRef = useRef<HTMLDivElement>(null);
	const [searchQuery, setSearchQuery] = useState('');
	return (
		<div ref={containerRef} className={clsx(styles.container, styles.containerCompact)}>
			<div className={styles.searchRow}>
				<Input
					placeholder={t`Search keybinds…`}
					leftIcon={<MagnifyingGlassIcon size={16} weight="bold" />}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					aria-label={t`Search keybinds`}
				/>
			</div>
			<CustomKeybindsList searchQuery={searchQuery} />
			<PortalShortcutsList />
			<InputMonitoringSection />
			<DefaultKeybindsList searchQuery={searchQuery} />
		</div>
	);
});

export default KeybindsTab;
